import React, { useState } from 'react';
import { CheckCircle2, Download, Trash2, ExternalLink, Facebook, Linkedin, Edit2, MessageSquare, ChevronDown, ChevronUp, AlertCircle, FileText, Search, User, Send, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { PeopleTab } from './PeopleTab';
import { FinancialsTab } from './FinancialsTab';
import { DetailsTab } from './DetailsTab';
import { NotificationsTab } from './NotificationsTab';
import { OverviewTab } from './OverviewTab';
import { AgentsTab } from './AgentsTab';
export function CreditApplication() {
  const [activeTab, setActiveTab] = useState('OVERVIEW');
  const [decisionOpen, setDecisionOpen] = useState(false);
  const [activeModal, setActiveModal] = useState<null | 'approve' | 'approveWithChanges' | 'reject'>(null);
  const [decisionMade, setDecisionMade] = useState<null | 'approved' | 'approvedWithChanges' | 'rejected'>(null);
  const [showAuditTrail, setShowAuditTrail] = useState(false);
  const [expandedRef, setExpandedRef] = useState<string | null>('Weyerhaeuser Paper');
  const handleDecision = (decision: 'approved' | 'approvedWithChanges' | 'rejected') => {
    setDecisionMade(decision);
    setActiveModal(null);
    setShowAuditTrail(true);
  };
  return <div className="min-h-full bg-gray-50 p-6" data-id="element-180">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-181">
        {/* Header Card */}
        <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4" data-id="element-182">
          <div className="flex items-center justify-between gap-4" data-id="element-183">
            {/* Left: Company Info + Notes */}
            <div className="shrink-0" data-id="element-184">
              <h1 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-185">
                DUNDER MIFFLIN PAPER COMPANY
              </h1>
              <div className="flex items-center gap-3 text-indigo-600 mb-2" data-id="element-186">
                <ExternalLink className="w-3.5 h-3.5 cursor-pointer" data-id="element-187" />
                <Facebook className="w-3.5 h-3.5 cursor-pointer" data-id="element-188" />
                <Linkedin className="w-3.5 h-3.5 cursor-pointer" data-id="element-189" />
                <Edit2 className="w-3.5 h-3.5 cursor-pointer" data-id="element-190" />
              </div>
              <div className="space-y-0.5 text-xs text-gray-600 mb-2" data-id="element-191">
                <div className="flex items-center gap-1.5" data-id="element-192">
                  <span className="font-semibold text-gray-900" data-id="element-193">
                    Credit Manager:
                  </span>
                  <span data-id="element-194">Dave Davidson</span>
                  <Edit2 className="w-3 h-3 text-indigo-600 cursor-pointer" data-id="element-195" />
                </div>
                <div className="flex items-center gap-1.5" data-id="element-196">
                  <span className="font-semibold text-gray-900" data-id="element-197">Added:</span>
                  <span data-id="element-198">3/27/2026</span>
                </div>
              </div>
              <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-2.5 py-1 rounded flex items-center gap-1.5 text-xs font-medium" data-id="element-199">
                <MessageSquare className="w-3 h-3" data-id="element-200" />
                NOTES
              </button>
            </div>

            {/* Right: Risk Gauge */}
            <div className="flex flex-col items-center shrink-0 mr-4" data-id="element-201">
              <div className="flex items-end gap-2 mb-1" data-id="element-202">
                <span className="text-xs text-gray-400 font-medium" data-id="element-203">0</span>
                <div className="relative w-64" data-id="element-204">
                  {/* Score label */}
                  <div className="absolute -top-6 left-[82%] -translate-x-1/2 bg-green-100 text-green-800 text-xs font-bold px-2 py-0.5 rounded" data-id="element-205">
                    82
                  </div>
                  {/* Bar */}
                  <div className="relative w-full h-6 flex rounded-full overflow-hidden" data-id="element-206">
                    <div className="w-1/3 bg-red-300" data-id="element-207"></div>
                    <div className="w-1/3 bg-yellow-300" data-id="element-208"></div>
                    <div className="w-1/3 bg-green-300" data-id="element-209"></div>
                  </div>
                  {/* Indicator line */}
                  <div className="absolute top-0 bottom-0 left-[82%] w-0.5 flex flex-col items-center" data-id="element-210">
                    <div className="w-[3px] h-full bg-gray-700 rounded-full" style={{
                    borderLeft: '2px dashed #374151'
                  }} data-id="element-211"></div>
                  </div>
                </div>
                <span className="text-xs text-gray-400 font-medium" data-id="element-212">100</span>
              </div>
              <div className="w-64 flex justify-between px-0 mt-1" data-id="element-213">
                <span className="text-xs text-red-500 font-medium pl-7" data-id="element-214">
                  Low
                </span>
                <span className="text-xs text-yellow-600 font-medium" data-id="element-215">
                  Medium
                </span>
                <span className="text-xs text-gray-600 font-medium pr-7" data-id="element-216">
                  High
                </span>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex border-t border-gray-200 mt-4 pt-3" data-id="element-217">
            {['OVERVIEW', 'NOTIFICATIONS', 'DETAILS', 'FINANCIALS', 'PEOPLE', 'APPLICATIONS', 'AGENTS'].map(tab => <button key={tab} onClick={() => setActiveTab(tab)} className={`flex-1 text-center py-1.5 text-xs font-semibold ${tab === activeTab ? 'text-indigo-600 border-b-2 border-indigo-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-218">
                {tab}
              </button>)}
          </div>
        </div>

        {/* Credit Decision Section */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg shadow-sm overflow-hidden" data-id="element-219">
          {/* Header Row */}
          <button onClick={() => setDecisionOpen(!decisionOpen)} className="w-full p-4 flex items-center justify-between bg-indigo-100 hover:bg-indigo-150 transition-colors" data-id="element-220">
            <div className="flex items-center gap-4" data-id="element-221">
              <span className="font-bold text-gray-900" data-id="element-222">Credit Decision</span>
              <span className="border border-green-500 text-green-600 px-2.5 py-0.5 rounded-full text-sm font-medium" data-id="element-223">
                Ready
              </span>
              <span className="text-sm text-gray-500" data-id="element-224">
                Date: March 27, 2026
              </span>
              <span className="text-sm text-gray-300" data-id="element-225">·</span>
              <span className="text-sm font-medium text-gray-700" data-id="element-226">
                Recommended Credit Limit: $50,000
              </span>
            </div>
            {decisionOpen ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-227" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-228" />}
          </button>

          {/* Expanded Content */}
          {decisionOpen && <div className="p-6 border-t border-indigo-200" data-id="element-229">
              <div className="grid grid-cols-2 gap-x-12 gap-y-6 mb-8" data-id="element-230">
                {/* Left Column */}
                <div className="space-y-4" data-id="element-231">
                  <div data-id="element-232">
                    <p className="text-sm text-gray-500 mb-1" data-id="element-233">
                      Recommended Decision
                    </p>
                    <p className="text-xl font-bold text-gray-900" data-id="element-234">
                      Approve for $50,000
                    </p>
                  </div>
                  <div data-id="element-235">
                    <p className="text-sm font-medium text-gray-700 mb-1" data-id="element-236">
                      Terms
                    </p>
                    <div className="text-sm text-gray-600 space-y-0.5" data-id="element-237">
                      <p data-id="element-238">Payment Terms Days: 30</p>
                      <p data-id="element-239">Discount: 5%</p>
                      <p data-id="element-240">Discount Days: 10</p>
                      <p data-id="element-241">Late Pay Penalty: 1.5%</p>
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-4" data-id="element-242">
                  <div data-id="element-243">
                    <div className="flex items-center gap-2 mb-1" data-id="element-244">
                      <p className="text-sm text-gray-500" data-id="element-245">Policy Applied</p>
                      <span className="text-sm text-gray-300" data-id="element-246">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-247">
                        (View Policy)
                      </button>
                    </div>
                    <p className="font-bold text-gray-900" data-id="element-248">
                      My Company's Credit Policy v1
                    </p>
                  </div>

                  <div data-id="element-249">
                    <div className="flex items-center gap-2 mb-1" data-id="element-250">
                      <p className="text-sm text-gray-500" data-id="element-251">
                        Credit Application
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-252">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-253">
                        (View Application)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700" data-id="element-254">
                      Submitted on: March 27, 2026
                    </p>
                  </div>

                  <div data-id="element-255">
                    <div className="flex items-center gap-2 mb-1" data-id="element-256">
                      <p className="text-sm text-gray-500" data-id="element-257">
                        Recommendation Summary
                      </p>
                      <span className="text-sm text-gray-300" data-id="element-258">·</span>
                      <button className="text-sm text-indigo-600 hover:text-indigo-700 italic" data-id="element-259">
                        (Full Reasoning)
                      </button>
                    </div>
                    <p className="text-sm text-gray-700 leading-relaxed" data-id="element-260">
                      Dunder Mifflin Paper Company, Inc. is approved for credit
                      based on strong business fundamentals, an established
                      77-year operating history, and positive trade references.
                      However, the credit limit is conservatively set due to the
                      applicant's limited initial order size and the need to
                      establish a direct payment history, warranting a measured
                      initial credit exposure.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="grid grid-cols-4 gap-4" data-id="element-261">
                <button onClick={() => setActiveModal('approve')} className="bg-green-700 hover:bg-green-800 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-262">
                  APPROVE
                </button>
                <button onClick={() => setActiveModal('approveWithChanges')} className="bg-yellow-600 hover:bg-yellow-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-263">
                  APPROVE WITH CHANGES
                </button>
                <button onClick={() => setActiveModal('reject')} className="bg-red-600 hover:bg-red-700 text-white py-3 rounded-lg font-semibold text-sm" data-id="element-264">
                  REJECT
                </button>
                <button className="border-2 border-indigo-600 text-indigo-600 hover:bg-indigo-50 py-3 rounded-lg font-semibold text-sm" data-id="element-265">
                  REGENERATE RECOMMENDATION
                </button>
              </div>
            </div>}
        </div>

        {/* Decision Modals */}
        <AnimatePresence data-id="element-266">
          {activeModal && <>
              {/* Backdrop */}
              <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} exit={{
            opacity: 0
          }} onClick={() => setActiveModal(null)} className="fixed inset-0 bg-black/50 z-50" data-id="element-267" />

              {/* Modal */}
              <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} exit={{
            opacity: 0,
            scale: 0.95
          }} className="fixed inset-0 z-50 flex items-center justify-center p-4" data-id="element-268">
                <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full p-8" data-id="element-269">
                  {activeModal === 'approve' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-270">
                        Approve Recommendation
                      </h2>
                      <div className="space-y-6" data-id="element-271">
                        <div className="grid grid-cols-3 gap-4" data-id="element-272">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-273">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-274">
                              Credit Limit
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-275" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-276">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-277">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-278">
                              <option data-id="element-279">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-280">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-281">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-282" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-283">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-284">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-285">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-286" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-287">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-288">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-289" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-290">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-291">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-292" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-293">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-294">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-295" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-296">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-297">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approved')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-298">
                          APPROVE
                        </button>
                      </div>
                    </>}

                  {activeModal === 'approveWithChanges' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-299">
                        Approve and Input Values
                      </h2>
                      <div className="space-y-6" data-id="element-300">
                        <div className="grid grid-cols-3 gap-4" data-id="element-301">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3 col-span-2" data-id="element-302">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-303">
                              Credit Limit *
                            </legend>
                            <input type="text" defaultValue="50,000" className="w-full text-lg text-gray-900 outline-none" data-id="element-304" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-305">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-306">
                              Currency
                            </legend>
                            <select className="w-full text-lg text-gray-900 outline-none bg-transparent" data-id="element-307">
                              <option data-id="element-308">USD</option>
                            </select>
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-309">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-310">
                            Payment Terms Days
                          </legend>
                          <input type="text" defaultValue="30" className="w-full text-lg text-gray-900 outline-none" data-id="element-311" />
                        </fieldset>
                        <div className="grid grid-cols-2 gap-4" data-id="element-312">
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-313">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-314">
                              Discount (%)
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-315" />
                          </fieldset>
                          <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-316">
                            <legend className="text-sm text-gray-500 px-1" data-id="element-317">
                              Discount Days
                            </legend>
                            <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-318" />
                          </fieldset>
                        </div>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-319">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-320">
                            Late Pay Penalty (%)
                          </legend>
                          <input type="text" placeholder="" className="w-full text-lg text-gray-900 outline-none" data-id="element-321" />
                        </fieldset>
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-322">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-323">
                            Reasoning
                          </legend>
                          <textarea rows={4} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-324" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-325">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-326">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('approvedWithChanges')} className="bg-indigo-600 hover:bg-indigo-700 text-white font-semibold px-8 py-3 rounded" data-id="element-327">
                          CONFIRM APPROVAL
                        </button>
                      </div>
                    </>}

                  {activeModal === 'reject' && <>
                      <h2 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-328">
                        Reject
                      </h2>
                      <div className="space-y-6" data-id="element-329">
                        <fieldset className="border border-gray-300 rounded px-3 pt-2 pb-3" data-id="element-330">
                          <legend className="text-sm text-gray-500 px-1" data-id="element-331">
                            Reasoning
                          </legend>
                          <textarea rows={6} className="w-full text-lg text-gray-900 outline-none resize-none" data-id="element-332" />
                        </fieldset>
                      </div>
                      <div className="flex justify-end gap-4 mt-8" data-id="element-333">
                        <button onClick={() => setActiveModal(null)} className="text-indigo-600 font-semibold px-6 py-3 hover:bg-indigo-50 rounded" data-id="element-334">
                          CANCEL
                        </button>
                        <button onClick={() => handleDecision('rejected')} className="bg-red-600 hover:bg-red-700 text-white font-semibold px-8 py-3 rounded" data-id="element-335">
                          CONFIRM REJECTION
                        </button>
                      </div>
                    </>}
                </div>
              </motion.div>
            </>}
        </AnimatePresence>

        {/* Audit Trail */}
        <AnimatePresence data-id="element-336">
          {showAuditTrail && <motion.div initial={{
          opacity: 0,
          height: 0
        }} animate={{
          opacity: 1,
          height: 'auto'
        }} exit={{
          opacity: 0,
          height: 0
        }} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-337">
              <div className="p-6" data-id="element-338">
                <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-339">
                  Audit Trail
                </h3>
                <div className="space-y-6" data-id="element-340">
                  {/* Application Submitted */}
                  <div className="flex gap-4" data-id="element-341">
                    <div className="flex flex-col items-center" data-id="element-342">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5" data-id="element-343" />
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-344" />
                    </div>
                    <div className="flex-1 pb-6" data-id="element-345">
                      <div className="flex items-center gap-2 mb-1" data-id="element-346">
                        <h4 className="font-semibold text-gray-900" data-id="element-347">
                          Application Submitted
                        </h4>
                        <span className="text-xs text-gray-500" data-id="element-348">
                          March 27, 2026, 10:15 AM
                        </span>
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-349">by Pam Beesly</p>
                    </div>
                  </div>

                  {/* Assigned to Credit Manager */}
                  <div className="flex gap-4" data-id="element-350">
                    <div className="flex flex-col items-center" data-id="element-351">
                      <div className="w-3 h-3 rounded-full bg-blue-500 mt-1.5" data-id="element-352" />
                      <div className="w-0.5 h-full bg-gray-200 mt-2" data-id="element-353" />
                    </div>
                    <div className="flex-1 pb-6" data-id="element-354">
                      <div className="flex items-center gap-2 mb-1" data-id="element-355">
                        <h4 className="font-semibold text-gray-900" data-id="element-356">
                          Assigned to Credit Manager
                        </h4>
                        <span className="text-xs text-gray-500" data-id="element-357">
                          March 27, 2026, 10:16 AM
                        </span>
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-358">
                        assigned to Dave Davidson
                      </p>
                    </div>
                  </div>

                  {/* Decision Entry */}
                  <div className="flex gap-4" data-id="element-359">
                    <div className="flex flex-col items-center" data-id="element-360">
                      <div className={`w-3 h-3 rounded-full mt-1.5 ${decisionMade === 'approved' ? 'bg-green-500' : decisionMade === 'approvedWithChanges' ? 'bg-yellow-500' : 'bg-red-500'}`} data-id="element-361" />
                    </div>
                    <div className="flex-1" data-id="element-362">
                      <div className="flex items-center gap-2 mb-1" data-id="element-363">
                        <h4 className="font-semibold text-gray-900" data-id="element-364">
                          {decisionMade === 'approved' && 'Credit Approved for $50,000'}
                          {decisionMade === 'approvedWithChanges' && 'Credit Approved with Modifications'}
                          {decisionMade === 'rejected' && 'Credit Application Rejected'}
                        </h4>
                        <span className="text-xs text-gray-500" data-id="element-365">
                          March 29, 2026, 2:34 PM
                        </span>
                      </div>
                      <p className="text-sm text-gray-600" data-id="element-366">by Dave Davidson</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>}
        </AnimatePresence>

        {activeTab === 'OVERVIEW' ? <OverviewTab data-id="element-367" /> : activeTab === 'FINANCIALS' ? <FinancialsTab data-id="element-368" /> : activeTab === 'PEOPLE' ? <PeopleTab data-id="element-369" /> : activeTab === 'DETAILS' ? <DetailsTab data-id="element-370" /> : activeTab === 'NOTIFICATIONS' ? <NotificationsTab data-id="element-371" /> : activeTab === 'AGENTS' ? <AgentsTab data-id="element-372" /> : activeTab === 'APPLICATIONS' ? <div className="bg-white rounded-lg border border-gray-200 shadow-sm" data-id="element-373">
            {/* Header */}
            <div className="p-6 border-b border-gray-200 flex justify-between items-start" data-id="element-374">
              <div data-id="element-375">
                <div className="flex items-center gap-3 mb-2" data-id="element-376">
                  <h2 className="text-xl font-bold text-gray-900" data-id="element-377">
                    Credit Application
                  </h2>
                  <span className="border border-indigo-200 text-indigo-700 bg-indigo-50 px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1.5" data-id="element-378">
                    <Search className="w-3.5 h-3.5" data-id="element-379" />
                    Ready for Review
                  </span>
                </div>
                <div className="text-sm text-gray-600 space-y-1" data-id="element-380">
                  <p data-id="element-381">
                    <span className="font-semibold text-gray-900" data-id="element-382">
                      Started On:
                    </span>{' '}
                    March 27, 2026
                  </p>
                  <p data-id="element-383">
                    <span className="font-semibold text-gray-900" data-id="element-384">
                      Submitted On:
                    </span>{' '}
                    March 27, 2026
                  </p>
                </div>
              </div>
              <button className="border border-indigo-200 text-indigo-600 hover:bg-indigo-50 px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-385">
                <Download className="w-4 h-4" data-id="element-386" />
                DOWNLOAD PDF
              </button>
            </div>

            {/* Applicant Details */}
            <div className="p-6 border-b border-gray-200" data-id="element-387">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-388">
                Applicant Details
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-389">
                <div data-id="element-390">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-391">Name</p>
                  <p className="text-gray-900" data-id="element-392">Pam Beesly</p>
                </div>
                <div data-id="element-393">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-394">Company</p>
                  <p className="text-gray-900" data-id="element-395">
                    Dunder Mifflin Paper Company Inc
                  </p>
                </div>
                <div data-id="element-396">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-397">Role</p>
                  <p className="text-gray-900" data-id="element-398">Office Administrator</p>
                </div>
                <div data-id="element-399">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-400">Department</p>
                  <p className="text-gray-900" data-id="element-401">Administration</p>
                </div>
                <div data-id="element-402">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-403">Email</p>
                  <div className="flex items-center gap-2 mb-3" data-id="element-404">
                    <p className="text-gray-900" data-id="element-405">
                      pam.beesly@dundermifflin.com
                    </p>
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-406" />
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3" data-id="element-407">
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" data-id="element-408" />
                    <div data-id="element-409">
                      <p className="font-semibold text-gray-900 text-sm" data-id="element-410">
                        Email Verification
                      </p>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-411">
                        Email verified and matches company domain.
                      </p>
                    </div>
                  </div>
                </div>
                <div data-id="element-412">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-413">Phone Number</p>
                  <p className="text-gray-900" data-id="element-414">(570) 555-0126</p>
                </div>
                <div data-id="element-415">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-416">
                    Location (IP Address)
                  </p>
                  <div className="flex items-center gap-2 mb-3" data-id="element-417">
                    <p className="text-gray-900" data-id="element-418">
                      Scranton, PA (136.61.36.131)
                    </p>
                    <CheckCircle2 className="w-4 h-4 text-green-500" data-id="element-419" />
                  </div>
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex gap-3" data-id="element-420">
                    <CheckCircle2 className="w-5 h-5 text-green-500 shrink-0" data-id="element-421" />
                    <div data-id="element-422">
                      <p className="font-semibold text-gray-900 text-sm" data-id="element-423">
                        IP Address Verification
                      </p>
                      <p className="text-sm text-gray-700 mt-1" data-id="element-424">
                        Applicant location matches company headquarters.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Business Contact Information */}
            <div className="p-6 border-b border-gray-200" data-id="element-425">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-426">
                Business Contact Information
              </h3>
              <div className="grid grid-cols-2 gap-12" data-id="element-427">
                <div data-id="element-428">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-429">
                    Legal Company Name
                  </p>
                  <p className="text-gray-900" data-id="element-430">
                    Dunder Mifflin Paper Company Inc
                  </p>
                </div>
                <div data-id="element-431">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-432">
                    Please attach W-9 Form below:
                  </p>
                  <div className="border border-indigo-200 rounded px-3 py-2 flex items-center gap-2 text-indigo-600 text-sm max-w-xs bg-white" data-id="element-433">
                    <FileText className="w-4 h-4" data-id="element-434" />
                    W9.svg
                  </div>
                </div>
              </div>
            </div>

            {/* Physical Address */}
            <div className="p-6 border-b border-gray-200" data-id="element-435">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-436">
                Physical Address
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-437">
                <div data-id="element-438">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-439">Street Address</p>
                  <p className="text-gray-900" data-id="element-440">1725 Slough Avenue</p>
                </div>
                <div data-id="element-441">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-442">City</p>
                  <p className="text-gray-900" data-id="element-443">Scranton</p>
                </div>
                <div data-id="element-444">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-445">State</p>
                  <p className="text-gray-900" data-id="element-446">PA</p>
                </div>
                <div data-id="element-447">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-448">Zip Code</p>
                  <p className="text-gray-900" data-id="element-449">18505</p>
                </div>
                <div data-id="element-450">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-451">Country</p>
                  <p className="text-gray-900" data-id="element-452">United States</p>
                </div>
              </div>
            </div>

            {/* Principals */}
            <div className="p-6 border-b border-gray-200" data-id="element-453">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-454">
                Principals
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-455">
                <div data-id="element-456">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-457">Full Name</p>
                  <p className="text-gray-900" data-id="element-458">Michael Scott</p>
                </div>
                <div data-id="element-459">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-460">Address</p>
                  <p className="text-gray-900" data-id="element-461">1725 Slough Avenue</p>
                </div>
                <div data-id="element-462">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-463">Position</p>
                  <p className="text-gray-900" data-id="element-464">Regional Manager</p>
                </div>
                <div data-id="element-465">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-466">Phone #</p>
                  <p className="text-gray-900" data-id="element-467">(570) 555-0126</p>
                </div>
              </div>
            </div>

            {/* Business Details */}
            <div className="p-6 border-b border-gray-200" data-id="element-468">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-469">
                Business Details
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-470">
                <div data-id="element-471">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-472">
                    Date Business Started
                  </p>
                  <p className="text-gray-900" data-id="element-473">1949-01-01</p>
                </div>
                <div data-id="element-474">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-475">Company Website</p>
                  <p className="text-gray-900" data-id="element-476">https://www.dundermifflin.com</p>
                </div>
                <div data-id="element-477">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-478">Business Type</p>
                  <p className="text-gray-900" data-id="element-479">Corporation</p>
                </div>
                <div data-id="element-480">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-481">
                    If "Other", please specify:
                  </p>
                  <p className="text-gray-900" data-id="element-482"></p>
                </div>
                <div data-id="element-483">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-484">
                    Accounting Contact Name
                  </p>
                  <p className="text-gray-900" data-id="element-485">Angela Martin</p>
                </div>
                <div data-id="element-486">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-487">
                    Accounting Contact Email
                  </p>
                  <p className="text-gray-900" data-id="element-488">
                    angela.martin@dundermifflin.com
                  </p>
                </div>
              </div>
            </div>

            {/* Trade References */}
            <div className="p-6 border-b border-gray-200" data-id="element-489">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-490">
                Trade References
              </h3>
              <div className="space-y-4 mb-6" data-id="element-491">
                {/* Weyerhaeuser Paper - Pending */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-492">
                  <button onClick={() => setExpandedRef(expandedRef === 'Weyerhaeuser Paper' ? null : 'Weyerhaeuser Paper')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-493">
                    <div data-id="element-494">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-495">
                        Weyerhaeuser Paper
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-496">
                        <span className="flex items-center gap-1.5" data-id="element-497">
                          <Calendar className="w-4 h-4" data-id="element-498" />
                          Pending since March 28, 2026
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-499">
                          <MessageSquare className="w-4 h-4" data-id="element-500" />1 email sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'Weyerhaeuser Paper' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-501" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-502" />}
                  </button>
                  <AnimatePresence data-id="element-503">
                    {expandedRef === 'Weyerhaeuser Paper' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-504">
                        <div className="p-6 space-y-6" data-id="element-505">
                          <div data-id="element-506">
                            <div className="flex items-center gap-2 mb-4" data-id="element-507">
                              <User className="w-5 h-5 text-gray-700" data-id="element-508" />
                              <h5 className="font-bold text-gray-900" data-id="element-509">
                                Reference Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-510" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-511">
                              <div data-id="element-512">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-513">
                                  Company Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-514">
                                  Weyerhaeuser Paper
                                </p>
                              </div>
                              <div data-id="element-515">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-516">
                                  Contact Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-517">
                                  Todd Packer
                                </p>
                              </div>
                              <div data-id="element-518">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-519">
                                  Contact Phone Number
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-520">
                                  (570) 555-0142
                                </p>
                              </div>
                              <div data-id="element-521">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-522">
                                  Contact Work Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-523">
                                  t.packer@weyerhaeuser.com
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-524">
                            <h5 className="font-bold text-gray-900 mb-2" data-id="element-525">
                              Awaiting Response
                            </h5>
                            <p className="text-sm text-gray-600 mb-4" data-id="element-526">
                              The reference request has been sent 1 times.
                            </p>
                            <button className="flex items-center gap-2 px-4 py-2 border border-indigo-300 text-indigo-600 bg-white hover:bg-indigo-50 rounded-md text-sm font-medium transition-colors" data-id="element-527">
                              <Send className="w-4 h-4" data-id="element-528" />
                              SEND REMINDER
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Hammermill - Completed */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-529">
                  <button onClick={() => setExpandedRef(expandedRef === 'Hammermill' ? null : 'Hammermill')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-530">
                    <div data-id="element-531">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-532">
                        Hammermill
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-533">
                        <span className="flex items-center gap-1.5" data-id="element-534">
                          <Calendar className="w-4 h-4" data-id="element-535" />
                          Completed on March 27, 2026
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-536">
                          <MessageSquare className="w-4 h-4" data-id="element-537" />1 email sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'Hammermill' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-538" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-539" />}
                  </button>
                  <AnimatePresence data-id="element-540">
                    {expandedRef === 'Hammermill' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-541">
                        <div className="p-6 space-y-6" data-id="element-542">
                          <div data-id="element-543">
                            <div className="flex items-center gap-2 mb-4" data-id="element-544">
                              <User className="w-5 h-5 text-gray-700" data-id="element-545" />
                              <h5 className="font-bold text-gray-900" data-id="element-546">
                                Reference Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-547" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-548">
                              <div data-id="element-549">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-550">
                                  Company Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-551">
                                  Hammermill
                                </p>
                              </div>
                              <div data-id="element-552">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-553">
                                  Contact Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-554">
                                  Tom Halpert
                                </p>
                              </div>
                              <div data-id="element-555">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-556">
                                  Contact Phone Number
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-557">
                                  (215) 555-0188
                                </p>
                              </div>
                              <div data-id="element-558">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-559">
                                  Contact Work Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-560">
                                  t.halpert@hammermill.com
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-561">
                            <div className="flex items-center gap-2 mb-2" data-id="element-562">
                              <h5 className="font-bold text-gray-900" data-id="element-563">
                                Reference Completed
                              </h5>
                              <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-564" />
                            </div>
                            <p className="text-sm text-gray-600 mb-6" data-id="element-565">
                              Response received on March 27, 2026
                            </p>
                            <div className="grid grid-cols-2 gap-y-4 gap-x-12" data-id="element-566">
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-567">
                                <span className="text-sm text-gray-500" data-id="element-568">
                                  Years as Customer
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-569">
                                  12
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-570">
                                <span className="text-sm text-gray-500" data-id="element-571">
                                  Credit Limit
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-572">
                                  $75,000
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-573">
                                <span className="text-sm text-gray-500" data-id="element-574">
                                  Current Balance
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-575">
                                  $8,200
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-576">
                                <span className="text-sm text-gray-500" data-id="element-577">
                                  Payment Habits
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-578">
                                  Prompt / Within Terms
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-579">
                                <span className="text-sm text-gray-500" data-id="element-580">
                                  Recommended
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-581">
                                  Yes
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>

                {/* Sabre - Not Sent */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-582">
                  <button onClick={() => setExpandedRef(expandedRef === 'Sabre' ? null : 'Sabre')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-583">
                    <div data-id="element-584">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-585">
                        Sabre
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-586">
                        <span className="flex items-center gap-1.5" data-id="element-587">
                          <Calendar className="w-4 h-4" data-id="element-588" />
                          Not Sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'Sabre' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-589" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-590" />}
                  </button>
                  <AnimatePresence data-id="element-591">
                    {expandedRef === 'Sabre' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-592">
                        <div className="p-6 space-y-6" data-id="element-593">
                          <div data-id="element-594">
                            <div className="flex items-center gap-2 mb-4" data-id="element-595">
                              <User className="w-5 h-5 text-gray-700" data-id="element-596" />
                              <h5 className="font-bold text-gray-900" data-id="element-597">
                                Reference Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-598" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-599">
                              <div data-id="element-600">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-601">
                                  Company Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-602">
                                  Sabre
                                </p>
                              </div>
                              <div data-id="element-603">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-604">
                                  Company Address
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-605">
                                  221 Sabre Way, Tallahassee FL 32301
                                </p>
                              </div>
                              <div data-id="element-606">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-607">
                                  Contact Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-608">
                                  Gabe Lewis
                                </p>
                              </div>
                              <div data-id="element-609">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-610">
                                  Contact Work Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-611">
                                  g.lewis@sabre.com
                                </p>
                              </div>
                              <div data-id="element-612">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-613">
                                  Contact Phone Number
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-614">
                                  (850) 555-0177
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-615">
                            <h5 className="font-bold text-gray-900 mb-2" data-id="element-616">
                              Ready to Send
                            </h5>
                            <p className="text-sm text-gray-600 mb-4" data-id="element-617">
                              This trade reference request has not yet been sent
                              to the contact.
                            </p>
                            <button className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white hover:bg-indigo-700 rounded-md text-sm font-medium transition-colors" data-id="element-618">
                              <Send className="w-4 h-4" data-id="element-619" />
                              SEND REFERENCE
                            </button>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-620">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-621">
                  Additional Trade References
                </h4>
                <p className="text-sm text-gray-500 mb-4" data-id="element-622">
                  No additional references requested
                </p>
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-623">
                  <ExternalLink className="w-4 h-4" data-id="element-624" />
                  REQUEST ADDITIONAL TRADE REFERENCES
                </button>
              </div>
            </div>

            {/* Personal Guarantors */}
            <div className="p-6 border-b border-gray-200" data-id="element-625">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-626">
                Personal Guarantors
              </h3>
              <div className="space-y-4 mb-6" data-id="element-627">
                {/* Michael Scott - Completed */}
                <div className="border border-gray-200 rounded-lg bg-white overflow-hidden" data-id="element-628">
                  <button onClick={() => setExpandedRef(expandedRef === 'guarantor-scott' ? null : 'guarantor-scott')} className="w-full p-4 flex justify-between items-center hover:bg-gray-50 transition-colors text-left" data-id="element-629">
                    <div data-id="element-630">
                      <h4 className="text-lg font-bold text-gray-900 mb-1.5" data-id="element-631">
                        Michael Scott
                      </h4>
                      <div className="flex items-center gap-4 text-sm text-gray-500" data-id="element-632">
                        <span className="flex items-center gap-1.5" data-id="element-633">
                          <Calendar className="w-4 h-4" data-id="element-634" />
                          Completed on March 27, 2026
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-635">
                          <MessageSquare className="w-4 h-4" data-id="element-636" />1 email sent
                        </span>
                      </div>
                    </div>
                    {expandedRef === 'guarantor-scott' ? <ChevronUp className="w-5 h-5 text-gray-400" data-id="element-637" /> : <ChevronDown className="w-5 h-5 text-gray-400" data-id="element-638" />}
                  </button>
                  <AnimatePresence data-id="element-639">
                    {expandedRef === 'guarantor-scott' && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-gray-100" data-id="element-640">
                        <div className="p-6 space-y-6" data-id="element-641">
                          <div data-id="element-642">
                            <div className="flex items-center gap-2 mb-4" data-id="element-643">
                              <User className="w-5 h-5 text-gray-700" data-id="element-644" />
                              <h5 className="font-bold text-gray-900" data-id="element-645">
                                Guarantor Information
                              </h5>
                              <Edit2 className="w-4 h-4 text-indigo-600 cursor-pointer" data-id="element-646" />
                            </div>
                            <div className="bg-gray-50 rounded-lg p-6 grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-647">
                              <div data-id="element-648">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-649">
                                  Full Name
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-650">
                                  Michael Scott
                                </p>
                              </div>
                              <div data-id="element-651">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-652">
                                  Title
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-653">
                                  Regional Manager
                                </p>
                              </div>
                              <div data-id="element-654">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-655">
                                  Email
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-656">
                                  m.scott@dundermifflin.com
                                </p>
                              </div>
                              <div data-id="element-657">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-658">
                                  Phone
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-659">
                                  (570) 555-0100
                                </p>
                              </div>
                              <div data-id="element-660">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-661">
                                  Address
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-662">
                                  1725 Slough Avenue, Scranton, PA 18503
                                </p>
                              </div>
                              <div data-id="element-663">
                                <p className="text-sm text-gray-500 mb-1" data-id="element-664">
                                  SSN
                                </p>
                                <p className="text-base font-medium text-gray-900" data-id="element-665">
                                  ***-**-4927
                                </p>
                              </div>
                            </div>
                          </div>
                          <div className="bg-gray-50 rounded-lg p-6" data-id="element-666">
                            <div className="flex items-center gap-2 mb-2" data-id="element-667">
                              <h5 className="font-bold text-gray-900" data-id="element-668">
                                Guarantee Completed
                              </h5>
                              <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-669" />
                            </div>
                            <p className="text-sm text-gray-600 mb-6" data-id="element-670">
                              Personal guarantee signed on March 27, 2026
                            </p>
                            <div className="grid grid-cols-2 gap-y-4 gap-x-12" data-id="element-671">
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-672">
                                <span className="text-sm text-gray-500" data-id="element-673">
                                  Guarantee Amount
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-674">
                                  $50,000
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-675">
                                <span className="text-sm text-gray-500" data-id="element-676">
                                  Date Signed
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-677">
                                  March 27, 2026
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-678">
                                <span className="text-sm text-gray-500" data-id="element-679">
                                  Guarantee Type
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-680">
                                  Unlimited
                                </span>
                              </div>
                              <div className="flex justify-between border-b border-gray-200 pb-2" data-id="element-681">
                                <span className="text-sm text-gray-500" data-id="element-682">
                                  Expiration
                                </span>
                                <span className="text-sm font-medium text-gray-900" data-id="element-683">
                                  None
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6" data-id="element-684">
                <h4 className="font-bold text-gray-900 mb-1" data-id="element-685">
                  Additional Personal Guarantors
                </h4>
                <p className="text-sm text-gray-500 mb-4" data-id="element-686">
                  No additional guarantors requested
                </p>
                <button className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded text-sm font-medium flex items-center gap-2" data-id="element-687">
                  <Send className="w-4 h-4" data-id="element-688" />
                  REQUEST ADDITIONAL PERSONAL GUARANTORS
                </button>
              </div>
            </div>

            {/* Banking Information */}
            <div className="p-6 border-b border-gray-200" data-id="element-689">
              <h3 className="text-lg font-bold text-gray-900 mb-6" data-id="element-690">
                Banking Information
              </h3>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-691">
                <div data-id="element-692">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-693">Bank Name</p>
                  <p className="text-gray-900" data-id="element-694">Scranton National Bank</p>
                </div>
                <div data-id="element-695">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-696">Contact</p>
                  <p className="text-gray-900" data-id="element-697">Robert Lipton</p>
                </div>
                <div data-id="element-698">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-699">Type of Account</p>
                  <p className="text-gray-900" data-id="element-700">Checking</p>
                </div>
                <div data-id="element-701">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-702">Account #</p>
                  <p className="text-gray-900" data-id="element-703">481726350</p>
                </div>
                <div data-id="element-704">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-705">Email</p>
                  <p className="text-gray-900" data-id="element-706">r.lipton@scrantonnational.com</p>
                </div>
                <div data-id="element-707">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-708">City</p>
                  <p className="text-gray-900" data-id="element-709">Scranton</p>
                </div>
                <div data-id="element-710">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-711">State</p>
                  <p className="text-gray-900" data-id="element-712">PA</p>
                </div>
                <div data-id="element-713">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-714">Phone #</p>
                  <p className="text-gray-900" data-id="element-715">570-555-0198</p>
                </div>
                <div data-id="element-716">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-717">Fax #</p>
                  <p className="text-gray-900" data-id="element-718">570-555-0199</p>
                </div>
              </div>
            </div>

            {/* Signature */}
            <div className="p-6" data-id="element-719">
              <h3 className="text-lg font-bold text-gray-900 mb-4" data-id="element-720">
                Signature
              </h3>

              <div className="border-t border-indigo-200 pt-4 mb-6" data-id="element-721">
                <h4 className="font-bold text-gray-900 mb-3" data-id="element-722">
                  Terms &amp; Conditions
                </h4>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-4" data-id="element-723">
                  <p className="text-sm text-gray-700 leading-relaxed" data-id="element-724">
                    I understand that should my account become delinquent (terms
                    of sale are based upon date of invoice) and that if the
                    creditor elects to employ a third party to collect monies
                    due, my company will be responsible for not only the
                    original amount billed, but also all costs associated with
                    the collection efforts including but not limited to
                    reasonable attorney fees. Cash In Advance restrictions may
                    be placed on any past due account. Customer will notify the
                    creditor, in writing of any ownership changes or legal means
                    of doing business by the company under which credit was
                    established.
                  </p>
                </div>
                <p className="text-sm font-semibold text-gray-900 mb-6" data-id="element-725">
                  By signing the below you give Dunder Mifflin Paper Company
                  Inc., permission to check your bank and trade references.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-y-6 gap-x-12" data-id="element-726">
                <div data-id="element-727">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-728">Signature</p>
                  <div className="border border-gray-200 rounded-lg p-4 h-24 flex items-end" data-id="element-729">
                    <svg viewBox="0 0 300 80" className="w-56 h-16" data-id="element-730">
                      <path d="M10,60 C20,55 25,30 35,25 C45,20 50,50 60,45 C70,40 75,20 85,25 C95,30 100,55 110,50 C115,47 118,35 125,30 C132,25 140,40 150,35 C155,32 160,28 170,30 C180,32 185,45 195,40 C200,38 205,25 215,30 C220,33 225,45 230,42" fill="none" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" data-id="element-731" />
                    </svg>
                  </div>
                </div>
                <div data-id="element-732">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-733">Printed Name</p>
                  <p className="text-gray-900" data-id="element-734">Dwight K. Schrute</p>
                </div>
                <div data-id="element-735">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-736">Title</p>
                  <p className="text-gray-900" data-id="element-737">Assistant Regional Manager</p>
                </div>
                <div data-id="element-738">
                  <p className="text-sm text-gray-500 mb-1" data-id="element-739">Date</p>
                  <p className="text-gray-900" data-id="element-740">2026-03-27</p>
                </div>
              </div>
            </div>
          </div> : <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-12 text-center text-gray-500" data-id="element-741">
            Content for {activeTab} tab coming soon.
          </div>}
      </div>
    </div>;
}